/**
 * Souk Al Amana — product landing page interactions.
 *
 * Architecture note: every interaction below is bound with event
 * delegation on `document`, and every piece of UI state (which option is
 * selected, which bundle tier is active, which gallery image is active,
 * which FAQ is open) is read back out of the DOM (classes/data attributes)
 * rather than kept in JS closures. That is deliberate: Shopify's Theme
 * Editor reloads a section's HTML in place on every settings change
 * (`shopify:section:load`), and delegated + DOM-sourced state keeps working
 * across those reloads with no re-binding step required.
 */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ *
   * Money formatting — Shopify's standard public helper.
   * ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.Shopify && window.Shopify.money_format) || "${{amount}}";

    function defaultOption(opt, def) {
      return typeof opt === "undefined" || opt === null ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ",");
      decimal = defaultOption(decimal, ".");
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split("."),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
        centsPart = parts[1] ? decimal + parts[1] : "";
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  /**
   * Horizon's squeeze layout (desktop ≥990px) makes `.page-wrapper` the real
   * scrolling element instead of the window/html — `document.scrollingElement`
   * has `overflow: hidden` at that breakpoint (see assets/scroll-container.js).
   * Any programmatic scroll in this file has to target the SAME element that
   * layout actually scrolls, or it silently does nothing on desktop.
   */
  var SOUK_SQUEEZE_QUERY = window.matchMedia ? window.matchMedia("(min-width: 990px)") : null;
  function getSoukScrollContainer() {
    if (SOUK_SQUEEZE_QUERY && SOUK_SQUEEZE_QUERY.matches) {
      return qs(document, ".page-wrapper") || document.scrollingElement || document.documentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  /* ------------------------------------------------------------------ *
   * Reveal-on-scroll
   * ------------------------------------------------------------------ */
  var revealIO = null;
  function initReveal(scope) {
    if (!("IntersectionObserver" in window)) {
      qsa(scope, "[data-souk-reveal]").forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              revealIO.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.06 }
      );
    }
    qsa(scope, "[data-souk-reveal]:not(.is-visible)").forEach(function (el) {
      revealIO.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Mobile nav drawer
   * ------------------------------------------------------------------ */
  function openNav() {
    var overlay = qs(document, "[data-souk-nav-overlay]");
    if (overlay) overlay.hidden = false;
    document.documentElement.classList.add("souk-lock-scroll");
  }
  function closeNav() {
    var overlay = qs(document, "[data-souk-nav-overlay]");
    if (overlay) overlay.hidden = true;
    document.documentElement.classList.remove("souk-lock-scroll");
  }

  /* ------------------------------------------------------------------ *
   * Cart drawer
   * ------------------------------------------------------------------ */
  var Cart = {
    open: function () {
      var overlay = qs(document, "[data-souk-cart-overlay]");
      if (!overlay) return;
      overlay.hidden = false;
      closeNav();
      Cart.refresh();
    },
    close: function () {
      var overlay = qs(document, "[data-souk-cart-overlay]");
      if (overlay) overlay.hidden = true;
    },
    isOpen: function () {
      var overlay = qs(document, "[data-souk-cart-overlay]");
      return !!overlay && !overlay.hidden;
    },
    updateCount: function (count) {
      qsa(document, "[data-souk-cart-count]").forEach(function (el) {
        el.textContent = count;
      });
    },
    refresh: function () {
      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          return r.json();
        })
        .then(Cart.render)
        .catch(function () {
          /* network hiccup — leave last rendered state in place */
        });
    },
    render: function (cart) {
      Cart.updateCount(cart.item_count);
      var body = qs(document, "[data-souk-cart-body]");
      var subtotal = qs(document, "[data-souk-cart-subtotal]");
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      if (!body) return;

      if (!cart.items.length) {
        var emptyLabel = body.getAttribute("data-empty-label") || "Your cart is empty.";
        body.innerHTML = '<div class="souk-cart-empty">' + emptyLabel + "</div>";
        return;
      }

      var removeLabel = body.getAttribute("data-remove-label") || "Remove";
      var html = cart.items
        .map(function (item) {
          var img = item.image
            ? '<img src="' + item.image + '" alt="" loading="lazy" width="76" height="95">'
            : "";
          return (
            '<div class="souk-cart-line" data-souk-cart-line data-key="' +
            item.key +
            '">' +
            '<div class="souk-cart-line__img">' +
            img +
            "</div>" +
            '<div class="souk-cart-line__info">' +
            '<div class="souk-cart-line__top">' +
            '<span class="souk-cart-line__name">' +
            item.product_title +
            "</span>" +
            '<button type="button" class="souk-cart-line__remove" data-souk-cart-remove data-key="' +
            item.key +
            '">' +
            removeLabel +
            "</button>" +
            "</div>" +
            '<span class="souk-cart-line__variant">' +
            (item.variant_title || "") +
            "</span>" +
            '<div class="souk-cart-line__row">' +
            '<div class="souk-qty-stepper">' +
            '<button type="button" data-souk-cart-dec data-key="' +
            item.key +
            '" aria-label="Decrease quantity">−</button>' +
            "<span>" +
            item.quantity +
            "</span>" +
            '<button type="button" data-souk-cart-inc data-key="' +
            item.key +
            '" aria-label="Increase quantity">+</button>' +
            "</div>" +
            '<span class="souk-cart-line__total">' +
            formatMoney(item.final_line_price) +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        })
        .join("");
      body.innerHTML = html;
    },
    changeLine: function (key, quantity) {
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(Cart.render)
        .catch(function () {});
    },
  };

  /* ------------------------------------------------------------------ *
   * Color guide popup (image modal opened from the product hero's
   * "Color guide" / info button — see souk-product-hero.liquid)
   * ------------------------------------------------------------------ */
  function openColorGuide() {
    var overlay = qs(document, "[data-souk-color-guide-overlay]");
    if (!overlay) return;
    overlay.hidden = false;
    document.documentElement.classList.add("souk-lock-scroll");
  }
  function closeColorGuide() {
    var overlay = qs(document, "[data-souk-color-guide-overlay]");
    if (overlay) overlay.hidden = true;
    document.documentElement.classList.remove("souk-lock-scroll");
  }

  /* ------------------------------------------------------------------ *
   * FAQ accordion
   * ------------------------------------------------------------------ */
  function toggleFaq(item) {
    var wasOpen = item.classList.contains("is-open");
    var group = closest(item, "[data-souk-faq]");
    if (group) {
      qsa(group, "[data-souk-faq-item].is-open").forEach(function (openItem) {
        if (openItem !== item) {
          openItem.classList.remove("is-open");
          var btn = qs(openItem, "[data-souk-faq-btn]");
          if (btn) btn.setAttribute("aria-expanded", "false");
        }
      });
    }
    item.classList.toggle("is-open", !wasOpen);
    var thisBtn = qs(item, "[data-souk-faq-btn]");
    if (thisBtn) thisBtn.setAttribute("aria-expanded", String(!wasOpen));
  }

  /* ------------------------------------------------------------------ *
   * Product gallery
   * ------------------------------------------------------------------ */
  function setGalleryActive(galleryId, index) {
    qsa(document, '[data-souk-gallery-image][data-gallery="' + galleryId + '"]').forEach(function (img) {
      img.classList.toggle("is-active", img.getAttribute("data-index") === String(index));
    });
    qsa(document, '[data-souk-gallery-thumb][data-gallery="' + galleryId + '"]').forEach(function (thumb) {
      thumb.classList.toggle("is-active", thumb.getAttribute("data-index") === String(index));
    });
  }
  function setGalleryActiveByMedia(galleryId, mediaId) {
    if (!mediaId) return;
    var target = qs(
      document,
      '[data-souk-gallery-thumb][data-gallery="' + galleryId + '"][data-media-id="' + mediaId + '"]'
    );
    if (target) setGalleryActive(galleryId, target.getAttribute("data-index"));
  }

  /* ------------------------------------------------------------------ *
   * Product form: options + bundle quantity + price/variant sync
   * ------------------------------------------------------------------ */
  function getFormData(formEl) {
    try {
      return JSON.parse(qs(formEl, "[data-souk-variants-json]").textContent);
    } catch (e) {
      return [];
    }
  }

  function currentSelection(formEl) {
    var selection = [];
    qsa(formEl, "[data-souk-option-group]").forEach(function (group) {
      var index = parseInt(group.getAttribute("data-option-index"), 10);
      var selected = qs(group, ".souk-pill.is-selected");
      selection[index] = selected ? selected.getAttribute("data-value") : null;
    });
    return selection;
  }

  function findVariant(formEl, selection) {
    var variants = getFormData(formEl);
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < selection.length; j++) {
        if (selection[j] != null && v.options[j] !== selection[j]) {
          match = false;
          break;
        }
      }
      if (match) return v;
    }
    return null;
  }

  function currentBundleQty(formEl) {
    var selected = qs(formEl, "[data-souk-bundle].is-selected");
    return selected ? parseInt(selected.getAttribute("data-qty"), 10) : 1;
  }

  // Same idea as currentBundleQty, but for PRICE display (Add to cart
  // button, mobile sticky bar) — falls back to the cart-add qty when a
  // tier has no separate data-price-qty, so a tier can add more units to
  // cart than the price shown reflects (e.g. Buy 2 Get 1 Free: 3 added
  // to cart, price shown for 2 paid units).
  function currentBundlePriceQty(formEl) {
    var selected = qs(formEl, "[data-souk-bundle].is-selected");
    if (!selected) return 1;
    var priceQty = parseInt(selected.getAttribute("data-price-qty"), 10);
    if (priceQty) return priceQty;
    return parseInt(selected.getAttribute("data-qty"), 10) || 1;
  }

  // Used by the desktop sticky bar's <select>: it isn't a separate source
  // of truth, it just drives the SAME pill selection the option pills use,
  // so every other synced element (main price, gallery image, mobile pill,
  // this bar) updates through the normal syncProductForm() pass.
  function selectVariantById(formEl, variantId) {
    var variants = getFormData(formEl);
    var variant = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        variant = variants[i];
        break;
      }
    }
    if (!variant) return;
    qsa(formEl, "[data-souk-option-group]").forEach(function (group) {
      var index = parseInt(group.getAttribute("data-option-index"), 10);
      var value = variant.options[index];
      qsa(group, ".souk-pill").forEach(function (p) {
        p.classList.toggle("is-selected", p.getAttribute("data-value") === value);
      });
      var valueLabel = qs(group, "[data-souk-option-value]");
      if (valueLabel && value != null) valueLabel.textContent = value;
    });
    syncProductForm(formEl);
  }

  function syncProductForm(formEl) {
    if (!formEl) return;
    var selection = currentSelection(formEl);
    var variant = findVariant(formEl, selection);
    var qty = currentBundleQty(formEl);
    var priceQty = currentBundlePriceQty(formEl);
    var moneyFormat = formEl.getAttribute("data-money-format") || "${{amount}}";

    // Grey out any free-gift card the currently selected bundle tier
    // doesn't qualify for (data-min-qty on the gift vs. the selected
    // tier's qty) — data-driven, no gift hardcoded to a specific tier.
    qsa(formEl, "[data-souk-gift]").forEach(function (gift) {
      var minQty = parseInt(gift.getAttribute("data-min-qty"), 10) || 1;
      gift.classList.toggle("is-disabled", qty < minQty);
    });

    var idInput = qs(formEl, "[data-souk-variant-input]");
    var qtyInput = qs(formEl, "[data-souk-qty-input]");
    var addBtn = qs(formEl, "[data-souk-add-to-cart]");
    var priceEl = qs(formEl, "[data-souk-price]");
    var compareEl = qs(formEl, "[data-souk-compare]");
    var saveEl = qs(formEl, "[data-souk-save]");
    var ctaPriceEl = qs(formEl, "[data-souk-cta-price]");
    var ctaCompareEl = qs(formEl, "[data-souk-cta-compare]");
    var errorEl = qs(formEl, "[data-souk-form-error]");

    if (qtyInput) qtyInput.value = qty;

    // update every bundle card's own displayed total to the *real* price.
    // Price display uses data-price-qty when present (falls back to the
    // cart-add quantity data-qty) so a tier can add more units to cart
    // than it prices for on the page — e.g. a Buy 2 Get 1 Free tier that
    // adds 3 units to cart but only shows the price for the 2 paid units.
    //
    // The secondary line under that total is either a per-unit price
    // (data-souk-bundle-per-unit — set when the section's unit_label
    // setting is filled in, see souk-product-hero.liquid) or, for
    // products that haven't set a unit_label, the original struck-through
    // Shopify compare-at price (data-souk-bundle-compare). Only one of
    // the two exists per card, decided at render time, so this just
    // updates whichever one is present.
    qsa(formEl, "[data-souk-bundle]").forEach(function (card) {
      var tierQty = parseInt(card.getAttribute("data-qty"), 10) || 1;
      var tierPriceQty = parseInt(card.getAttribute("data-price-qty"), 10) || tierQty;
      var totalEl = qs(card, "[data-souk-bundle-total]");
      if (totalEl && variant) totalEl.textContent = formatMoney(variant.price * tierPriceQty, moneyFormat);

      var perUnitEl = qs(card, "[data-souk-bundle-per-unit]");
      if (perUnitEl && variant) {
        var unitLabel = card.getAttribute("data-unit-label") || "";
        if (tierQty > 1) {
          var perUnitCents = Math.round((variant.price * tierPriceQty) / tierQty);
          perUnitEl.textContent = formatMoney(perUnitCents, moneyFormat) + "/per " + unitLabel;
        } else {
          perUnitEl.textContent = "/per " + unitLabel;
        }
      } else {
        var cardCompareEl = qs(card, "[data-souk-bundle-compare]");
        if (cardCompareEl && variant) {
          var cardHasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          cardCompareEl.textContent = cardHasCompare ? formatMoney(variant.compare_at_price * tierPriceQty, moneyFormat) : "";
          cardCompareEl.hidden = !cardHasCompare;
        }
      }
    });

    if (!variant) {
      if (idInput) idInput.value = "";
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.setAttribute("data-state", "unavailable");
        var unavailableLabelEl = qs(addBtn, "[data-souk-cta-label]");
        var soldOutLabel = addBtn.getAttribute("data-sold-out-label");
        if (unavailableLabelEl && soldOutLabel) unavailableLabelEl.textContent = soldOutLabel;
      }
      var unavailableStickyBtn = qs(document, "[data-souk-sticky]");
      if (unavailableStickyBtn) {
        unavailableStickyBtn.disabled = true;
        var unavailableStickyLabelEl = qs(unavailableStickyBtn, "[data-souk-sticky-cta-label]");
        var unavailableStickySoldOut = unavailableStickyBtn.getAttribute("data-sold-out-label");
        if (unavailableStickyLabelEl && unavailableStickySoldOut) unavailableStickyLabelEl.textContent = unavailableStickySoldOut;
      }
      var unavailableDesktopBtn = qs(document, "[data-souk-sticky-desktop-add]");
      if (unavailableDesktopBtn) {
        unavailableDesktopBtn.disabled = true;
        var unavailableDesktopLabelEl = qs(document, "[data-souk-sticky-desktop-cta-label]");
        var unavailableDesktopSoldOut = unavailableDesktopBtn.getAttribute("data-sold-out-label");
        if (unavailableDesktopLabelEl && unavailableDesktopSoldOut) unavailableDesktopLabelEl.textContent = unavailableDesktopSoldOut;
      }
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price, moneyFormat);
    if (ctaPriceEl) ctaPriceEl.textContent = formatMoney(variant.price * priceQty, moneyFormat);

    var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (compareEl) {
      compareEl.textContent = hasCompare ? formatMoney(variant.compare_at_price, moneyFormat) : "";
      compareEl.hidden = !hasCompare;
    }
    if (ctaCompareEl) {
      ctaCompareEl.textContent = hasCompare ? formatMoney(variant.compare_at_price * priceQty, moneyFormat) : "";
      ctaCompareEl.hidden = !hasCompare;
    }
    if (saveEl) {
      if (hasCompare) {
        var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        saveEl.textContent = saveEl.getAttribute("data-template").replace("[pct]", pct);
        saveEl.hidden = pct <= 0;
      } else {
        saveEl.hidden = true;
      }
    }

    if (addBtn) {
      var ctaLabelEl = qs(addBtn, "[data-souk-cta-label]");
      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.setAttribute("data-state", "sold-out");
        var soldOutText = addBtn.getAttribute("data-sold-out-label");
        if (ctaLabelEl && soldOutText) ctaLabelEl.textContent = soldOutText;
      } else {
        addBtn.disabled = false;
        addBtn.setAttribute("data-state", "idle");
        var idleText = addBtn.getAttribute("data-idle-label");
        if (ctaLabelEl && idleText) ctaLabelEl.textContent = idleText;
      }
    }
    if (errorEl) errorEl.classList.remove("is-visible");

    var galleryId = formEl.getAttribute("data-gallery-id");
    if (galleryId && variant.featured_media_id) {
      setGalleryActiveByMedia(galleryId, variant.featured_media_id);
    }

    var stickyBtn = qs(document, "[data-souk-sticky]");
    if (stickyBtn) {
      var stickyPriceEl = qs(stickyBtn, "[data-souk-sticky-price]");
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price * priceQty, moneyFormat);

      var stickyLabelEl = qs(stickyBtn, "[data-souk-sticky-cta-label]");
      if (!variant.available) {
        stickyBtn.disabled = true;
        var stickySoldOutText = stickyBtn.getAttribute("data-sold-out-label");
        if (stickyLabelEl && stickySoldOutText) stickyLabelEl.textContent = stickySoldOutText;
      } else {
        stickyBtn.disabled = false;
        var stickyIdleText = stickyBtn.getAttribute("data-idle-label");
        if (stickyLabelEl && stickyIdleText) stickyLabelEl.textContent = stickyIdleText;
      }
    }

    // Desktop sticky bar: mirrors the UNIT price (not the qty-multiplied
    // bundle total the CTA/mobile pill show), same as the main price row,
    // since that's the row this bar visually stands in for.
    var desktopBar = qs(document, "[data-souk-sticky-desktop]");
    if (desktopBar) {
      var desktopPriceEl = qs(desktopBar, "[data-souk-sticky-desktop-price]");
      if (desktopPriceEl) desktopPriceEl.textContent = formatMoney(variant.price, moneyFormat);

      var desktopCompareEl = qs(desktopBar, "[data-souk-sticky-desktop-compare]");
      var desktopSaveEl = qs(desktopBar, "[data-souk-sticky-desktop-save]");
      if (hasCompare) {
        if (desktopCompareEl) {
          desktopCompareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          desktopCompareEl.hidden = false;
        }
        if (desktopSaveEl) {
          var desktopPct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          desktopSaveEl.textContent = desktopSaveEl.getAttribute("data-template").replace("[pct]", desktopPct);
          desktopSaveEl.hidden = desktopPct <= 0;
        }
      } else {
        if (desktopCompareEl) desktopCompareEl.hidden = true;
        if (desktopSaveEl) desktopSaveEl.hidden = true;
      }

      var desktopThumb = qs(desktopBar, "[data-souk-sticky-desktop-thumb]");
      if (desktopThumb && variant.featured_media_url) desktopThumb.src = variant.featured_media_url;

      var desktopSelect = qs(desktopBar, "[data-souk-sticky-desktop-variant]");
      if (desktopSelect && desktopSelect.value !== String(variant.id)) desktopSelect.value = variant.id;

      var desktopAddBtn = qs(desktopBar, "[data-souk-sticky-desktop-add]");
      if (desktopAddBtn) {
        var desktopLabelEl = qs(desktopAddBtn, "[data-souk-sticky-desktop-cta-label]");
        if (!variant.available) {
          desktopAddBtn.disabled = true;
          var desktopSoldOutText = desktopAddBtn.getAttribute("data-sold-out-label");
          if (desktopLabelEl && desktopSoldOutText) desktopLabelEl.textContent = desktopSoldOutText;
        } else {
          desktopAddBtn.disabled = false;
          var desktopIdleText = desktopAddBtn.getAttribute("data-idle-label");
          if (desktopLabelEl && desktopIdleText) desktopLabelEl.textContent = desktopIdleText;
        }
      }
    }
  }

  function submitProductForm(formEl) {
    var addBtn = qs(formEl, "[data-souk-add-to-cart]");
    var idInput = qs(formEl, "[data-souk-variant-input]");
    var qtyInput = qs(formEl, "[data-souk-qty-input]");
    var errorEl = qs(formEl, "[data-souk-form-error]");
    if (!idInput || !idInput.value) return;

    var idleLabel = addBtn ? addBtn.getAttribute("data-idle-label") || addBtn.textContent : "";
    var loadingLabel = addBtn ? addBtn.getAttribute("data-loading-label") || idleLabel : "";

    if (addBtn) {
      addBtn.disabled = true;
      if (loadingLabel) qs(addBtn, "[data-souk-cta-label]") && (qs(addBtn, "[data-souk-cta-label]").textContent = loadingLabel);
    }
    if (errorEl) errorEl.classList.remove("is-visible");

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: idInput.value, quantity: parseInt((qtyInput && qtyInput.value) || 1, 10) }],
      }),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function () {
        Cart.open();
      })
      .catch(function (err) {
        if (errorEl) {
          errorEl.textContent = (err && err.description) || errorEl.getAttribute("data-default-message") || "Something went wrong.";
          errorEl.classList.add("is-visible");
        }
      })
      .finally(function () {
        if (addBtn) {
          addBtn.disabled = false;
          var labelEl = qs(addBtn, "[data-souk-cta-label]");
          if (labelEl && idleLabel) labelEl.textContent = idleLabel;
          syncProductForm(formEl);
        }
      });
  }

  /* ------------------------------------------------------------------ *
   * Reviews carousel
   * ------------------------------------------------------------------ */
  function slideReviews(track, dir) {
    if (!track || !track.firstElementChild) return;
    var style = getComputedStyle(track);
    var gap = parseFloat(style.gap || style.columnGap || "14") || 14;
    var step = track.firstElementChild.getBoundingClientRect().width + gap;
    var max = track.scrollWidth - track.clientWidth;
    var rtl = getComputedStyle(track).direction === "rtl";
    var pos = Math.abs(track.scrollLeft);
    var next = pos + dir * step;
    if (next > max - 2) next = dir > 0 && pos >= max - 2 ? 0 : max;
    if (next < 0) next = max;
    track.scrollTo({ left: rtl ? -next : next, behavior: "smooth" });
  }

  function initReviewAutoplay(track) {
    if (!track || track.__soukAutoplay) return;
    track.__soukAutoplay = setInterval(function () {
      if (document.hidden || track.matches(":hover")) return;
      slideReviews(track, 1);
    }, 5200);
  }

  /* ------------------------------------------------------------------ *
   * Photo reviews — scroll-driven progress bar (segments live in
   * [data-souk-photo-reviews-progress], sibling of the scrollable
   * [data-souk-photo-reviews-track]).
   * ------------------------------------------------------------------ */
  function initPhotoReviewsProgress(track) {
    if (!track || track.__soukProgress) return;
    track.__soukProgress = true;
    var wrap = closest(track, "[data-souk-photo-reviews]");
    var progress = wrap && qs(wrap, "[data-souk-photo-reviews-progress]");
    if (!progress) return;
    var segs = qsa(progress, ".souk-photo-reviews__progress-seg");
    if (!segs.length) return;
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var ratio = max > 0 ? Math.abs(track.scrollLeft) / max : 0;
      var activeIndex = Math.min(segs.length - 1, Math.floor(ratio * segs.length));
      segs.forEach(function (seg, i) {
        seg.classList.toggle("is-active", i === activeIndex);
      });
    }
    track.addEventListener("scroll", update, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * Sticky mobile purchase bar
   * ------------------------------------------------------------------ */
  var stickyIO = null;
  function initSticky(scope) {
    var mobileBar = qs(scope, "[data-souk-sticky]");
    var desktopBar = qs(scope, "[data-souk-sticky-desktop]");
    var sentinel = qs(document, "[data-souk-sticky-sentinel]");
    if ((!mobileBar && !desktopBar) || !sentinel || !("IntersectionObserver" in window)) return;
    if (stickyIO) stickyIO.disconnect();
    stickyIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // isIntersecting is false both when the sentinel hasn't been
          // reached yet (still below the viewport) and once it's been
          // scrolled past (now above the viewport) — those need opposite
          // outcomes. Only the second case ("scrolled past it") should
          // reveal the bar, which boundingClientRect.top < 0 tells apart.
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          var show = scrolledPast && !Cart.isOpen();
          if (mobileBar) mobileBar.classList.toggle("is-visible", show);
          if (desktopBar) desktopBar.classList.toggle("is-visible", show);
        });
      },
      { threshold: 0 }
    );
    stickyIO.observe(sentinel);
  }

  /* ------------------------------------------------------------------ *
   * Delegated event listeners (bound once)
   * ------------------------------------------------------------------ */
  function onClick(e) {
    var t = e.target;

    if (closest(t, "[data-souk-nav-toggle]")) {
      openNav();
      return;
    }
    if (closest(t, "[data-souk-nav-close]") || (closest(t, "[data-souk-nav-overlay]") && t.hasAttribute("data-souk-nav-overlay"))) {
      closeNav();
      return;
    }
    if (closest(t, "[data-souk-nav-link]")) {
      closeNav();
    }

    if (closest(t, "[data-souk-cart-toggle]")) {
      e.preventDefault();
      Cart.open();
      return;
    }
    if (closest(t, "[data-souk-cart-close]") || (closest(t, "[data-souk-cart-overlay]") && t.hasAttribute("data-souk-cart-overlay"))) {
      Cart.close();
      return;
    }

    if (closest(t, "[data-souk-color-guide-toggle]")) {
      e.preventDefault();
      openColorGuide();
      return;
    }
    if (closest(t, "[data-souk-color-guide-close]") || (closest(t, "[data-souk-color-guide-overlay]") && t.hasAttribute("data-souk-color-guide-overlay"))) {
      closeColorGuide();
      return;
    }

    var removeBtn = closest(t, "[data-souk-cart-remove]");
    if (removeBtn) {
      Cart.changeLine(removeBtn.getAttribute("data-key"), 0);
      return;
    }
    var incBtn = closest(t, "[data-souk-cart-inc]");
    if (incBtn) {
      var line = closest(incBtn, "[data-souk-cart-line]");
      var qtyEl = line && qs(line, ".souk-qty-stepper span");
      var newQty = (qtyEl ? parseInt(qtyEl.textContent, 10) : 0) + 1;
      Cart.changeLine(incBtn.getAttribute("data-key"), newQty);
      return;
    }
    var decBtn = closest(t, "[data-souk-cart-dec]");
    if (decBtn) {
      var line2 = closest(decBtn, "[data-souk-cart-line]");
      var qtyEl2 = line2 && qs(line2, ".souk-qty-stepper span");
      var newQty2 = Math.max(0, (qtyEl2 ? parseInt(qtyEl2.textContent, 10) : 1) - 1);
      Cart.changeLine(decBtn.getAttribute("data-key"), newQty2);
      return;
    }

    var faqBtn = closest(t, "[data-souk-faq-btn]");
    if (faqBtn) {
      toggleFaq(closest(faqBtn, "[data-souk-faq-item]"));
      return;
    }

    var thumb = closest(t, "[data-souk-gallery-thumb]");
    if (thumb) {
      setGalleryActive(thumb.getAttribute("data-gallery"), thumb.getAttribute("data-index"));
      return;
    }

    var pill = closest(t, "[data-souk-option]");
    if (pill && !pill.disabled) {
      var group = closest(pill, "[data-souk-option-group]");
      qsa(group, ".souk-pill").forEach(function (p) {
        p.classList.remove("is-selected");
      });
      pill.classList.add("is-selected");
      var valueLabel = closest(group, "[data-souk-option]") && qs(group, "[data-souk-option-value]");
      if (valueLabel) valueLabel.textContent = pill.textContent;
      syncProductForm(closest(pill, "[data-souk-product-form]"));
      return;
    }

    var bundle = closest(t, "[data-souk-bundle]");
    if (bundle) {
      var bundleForm = closest(bundle, "[data-souk-product-form]");
      qsa(bundleForm, "[data-souk-bundle]").forEach(function (b) {
        b.classList.remove("is-selected");
      });
      bundle.classList.add("is-selected");
      syncProductForm(bundleForm);
      return;
    }

    var reviewPrev = closest(t, "[data-souk-reviews-prev]");
    if (reviewPrev) {
      slideReviews(qs(closest(reviewPrev, "[data-souk-reviews]"), "[data-souk-reviews-track]"), -1);
      return;
    }
    var reviewNext = closest(t, "[data-souk-reviews-next]");
    if (reviewNext) {
      slideReviews(qs(closest(reviewNext, "[data-souk-reviews]"), "[data-souk-reviews-track]"), 1);
      return;
    }

    var scrollToBuy = closest(t, "[data-souk-scroll-to-buy]");
    if (scrollToBuy) {
      e.preventDefault();
      var target = document.getElementById("souk-purchase");
      if (target) {
        var buyContainer = getSoukScrollContainer();
        var headerOffset = 90;
        var top = target.getBoundingClientRect().top + buyContainer.scrollTop - headerOffset;
        buyContainer.scrollTo({ top: top, behavior: "smooth" });
      }
      return;
    }

    var scrollToTop = closest(t, "[data-souk-scroll-to-top]");
    if (scrollToTop) {
      e.preventDefault();
      getSoukScrollContainer().scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    var stickyAdd = closest(t, "[data-souk-sticky-add]");
    if (stickyAdd) {
      var mainForm = qs(document, "[data-souk-product-form]");
      if (mainForm) submitProductForm(mainForm);
      return;
    }

    var stickyDesktopAdd = closest(t, "[data-souk-sticky-desktop-add]");
    if (stickyDesktopAdd) {
      var desktopMainForm = qs(document, "[data-souk-product-form]");
      if (desktopMainForm) submitProductForm(desktopMainForm);
      return;
    }
  }

  function onSubmit(e) {
    var form = closest(e.target, "[data-souk-product-form]");
    if (!form) return;
    e.preventDefault();
    submitProductForm(form);
  }

  function onChange(e) {
    var desktopSelect = closest(e.target, "[data-souk-sticky-desktop-variant]");
    if (desktopSelect) {
      var formEl = qs(document, "[data-souk-product-form]");
      if (formEl) selectVariantById(formEl, desktopSelect.value);
    }
  }

  document.addEventListener("click", onClick);
  document.addEventListener("submit", onSubmit);
  document.addEventListener("change", onChange);
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" || e.key === "Esc") closeColorGuide();
  });

  /* ------------------------------------------------------------------ *
   * Init / re-init (first load + Theme Editor section reloads)
   * ------------------------------------------------------------------ */
  function init(scope) {
    scope = scope || document;
    initReveal(scope);
    initSticky(scope);
    qsa(scope, "[data-souk-product-form]").forEach(syncProductForm);
    qsa(scope, "[data-souk-reviews-track]").forEach(initReviewAutoplay);
    qsa(scope, "[data-souk-photo-reviews-track]").forEach(initPhotoReviewsProgress);
    if (qs(scope, "[data-souk-cart-overlay]")) Cart.refresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
  document.addEventListener("shopify:section:unload", function () {
    if (stickyIO) stickyIO.disconnect();
  });
  document.addEventListener("shopify:block:select", function (e) {
    var faqItem = closest(e.target, "[data-souk-faq-item]");
    if (faqItem) toggleFaq(faqItem);
  });
})();
